import { ArrowRight, Battery, BatteryCharging, BatteryWarning, BatteryFull } from "lucide-react";
import { Link } from "react-router-dom";
import type { BessUnitSnapshot } from "@/data/types";
import { useNow } from "@/hooks/useNow";
import { useSiteStore } from "@/store/site";
import { Card } from "@/components/ui/Card";
import { SocArc } from "@/components/ui/SocArc";
import { classifyAge, LIVE_WARN_AFTER_S, LIVE_STALE_AFTER_S } from "@/components/ui/FreshnessPip";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { signedKW, formatV, formatRelativeAge, isIdlePower } from "@/lib/format";

export function BessTile({ unit, capacity_kWh }: { unit: BessUnitSnapshot; capacity_kWh: number }) {
  const t = useT();
  // Unified idle noise floor (see format.ts:IDLE_DEADBAND_W) — a unit
  // sitting in standby/sensor-noise must not read as charging.
  const idle = isIdlePower(unit.active_W);

  // DATA-08 (visible half): per-unit telemetry freshness. The live assembler
  // stamps "bess.<id>.active_W" with the SOURCE ts of the last finite
  // battery_kW; the unit flips offline only after 15 min, so a unit can sit
  // "online" showing a power figure that is minutes old. Surface that gap
  // (>150 s) as an amber dot next to the value. No map (mock) or missing
  // key → no claim; offline units keep their banner — don't double-signal.
  const freshness = useSiteStore((s) => s.snapshot.freshness);
  const now = useNow(15_000);
  const powerTs = freshness?.[`bess.${unit.id}.active_W`];
  const powerStaleAgeMs =
    unit.online &&
    powerTs !== undefined &&
    classifyAge(now - powerTs, LIVE_WARN_AFTER_S, LIVE_STALE_AFTER_S) !== "fresh"
      ? now - powerTs
      : null;
  const charging = !idle && unit.active_W > 0;
  const discharging = !idle && unit.active_W < 0;
  const Icon = !unit.online ? BatteryWarning : charging ? BatteryCharging : unit.soc_pct > 95 ? BatteryFull : Battery;
  const tone = !unit.online ? "border-critical/40" : unit.fault_count > 0 ? "border-warn/40" : "border-edge";

  // carryTo: a tile click keeps the operator's time window (BESS-05).
  return (
    <Link to={carryTo(`/bess/${unit.id}`)} className="block">
      <Card className={cn("card-hover relative p-4 transition", tone)}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Icon size={20} className={!unit.online ? "text-critical" : charging ? "text-charge" : discharging ? "text-discharge" : "text-text-muted"} />
            <div>
              <div className="text-sm font-semibold tracking-tight">{unit.name}</div>
              <div className="text-[10px] text-text-muted">{t("monitor.bess.nominal", { kwh: capacity_kWh })}</div>
            </div>
          </div>
          <SocArc pct={unit.soc_pct} size={48} />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase text-text-faint">{t("monitor.bess.power")}</div>
            <div className={cn(
              "text-2xl font-bold num",
              charging && "text-charge",
              discharging && "text-discharge",
              !unit.online && "text-stale",
            )}>
              {unit.online ? signedKW(unit.active_W) : t("monitor.bess.off")}
              {unit.online && <span className="ml-1 text-xs text-text-muted">kW</span>}
              {powerStaleAgeMs !== null && (
                <span
                  className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-warn align-middle"
                  title={t("monitor.bess.staleTitle", { age: formatRelativeAge(powerStaleAgeMs) })}
                />
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-text-faint">{t("monitor.bess.dcBus")}</div>
            <div className="text-sm font-semibold num">{unit.online ? formatV(unit.total_voltage_V) : "—"}</div>
            <div className="text-[10px] text-text-muted">{unit.online ? `${unit.ambient_temperature_C.toFixed(1)} °C` : "—"}</div>
          </div>
        </div>

        {unit.fault_summary && (
          <div className="mt-3 rounded-md border border-warn/30 bg-warn/10 px-2 py-1.5 text-[10px] text-warn">
            <strong>{t("monitor.bess.fault")}</strong> {unit.fault_summary}
          </div>
        )}
        {!unit.online && (
          <div className="mt-3 rounded-md border border-critical/30 bg-critical/10 px-2 py-1.5 text-[10px] text-critical">
            {/* 0 = no heartbeat received yet; 9999 = mock-adapter sentinel.
                Neither is a real age, so don't render one. */}
            {unit.heartbeat_age_s > 0 && unit.heartbeat_age_s !== 9999
              ? t("monitor.bess.offlineHeartbeat", { age: formatRelativeAge(unit.heartbeat_age_s * 1000) })
              : t("monitor.bess.offlineNoData")}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-[11px] text-text-muted">
          <span className="flex items-center gap-1">
            <span className={cn("pip", unit.online ? "bg-ok live" : "bg-critical")} /> {unit.online ? t("monitor.bess.live") : t("monitor.bess.offline")}
          </span>
          <span className="flex items-center gap-1">{t("monitor.bess.inspect")} <ArrowRight size={10} /></span>
        </div>
      </Card>
    </Link>
  );
}
